/**
 * Responding resources & capability model — pure, I/O-free.
 *
 * Multi-appliance, multi-brigade: capability (total crew, BA operators as a
 * subset of that crew, pumps, onboard water) is aggregated across every
 * committed unit so the deterministic rules can compare it to doctrine
 * requirements.
 */

export const RESOURCE_STATUSES = [
  'responding',
  'on-scene',
  'staging',
  'returning',
  'unavailable',
] as const;
export type ResourceStatus = (typeof RESOURCE_STATUSES)[number];

export const RESOURCE_STATUS_LABELS: Record<ResourceStatus, string> = {
  responding: 'Responding',
  'on-scene': 'On scene',
  staging: 'Staging',
  returning: 'Returning',
  unavailable: 'Unavailable',
};

/** A unit is "committed" (counts toward capability) unless stood down. */
export const isCommitted = (s: ResourceStatus): boolean =>
  s !== 'returning' && s !== 'unavailable';

export const APPLIANCE_TYPES = [
  'tanker-cat1',
  'tanker-cat7',
  'tanker-cat9',
  'pumper',
  'pumper-tanker',
  'bulk-water',
  'support',
] as const;
export type ApplianceType = (typeof APPLIANCE_TYPES)[number];

export interface ApplianceSpec {
  label: string;
  /** Nominal onboard water (L) — a default the UI pre-fills; (stand-in figures). */
  defaultWaterL: number;
  /** Nominal pump rate (L/min). */
  pumpLpm: number;
  typicalCrew: number;
  /** Whether this appliance is a structural pumper (provides pumper capability). */
  isPumper: boolean;
}

/**
 * Appliance reference table (config-as-data, stand-in figures — review against
 * authoritative fleet data before real use). Pre-fills sensible defaults in the
 * UI; per-unit values can be overridden.
 */
export const APPLIANCE_REFERENCE: Record<ApplianceType, ApplianceSpec> = {
  'tanker-cat1': { label: 'Cat 1 Tanker', defaultWaterL: 3000, pumpLpm: 1000, typicalCrew: 5, isPumper: false },
  'tanker-cat7': { label: 'Cat 7 Tanker', defaultWaterL: 1000, pumpLpm: 500, typicalCrew: 3, isPumper: false },
  'tanker-cat9': { label: 'Cat 9 Tanker', defaultWaterL: 700, pumpLpm: 400, typicalCrew: 2, isPumper: false },
  pumper: { label: 'Pumper (FRNSW)', defaultWaterL: 1800, pumpLpm: 2000, typicalCrew: 4, isPumper: true },
  'pumper-tanker': { label: 'Pumper Tanker', defaultWaterL: 4000, pumpLpm: 2000, typicalCrew: 4, isPumper: true },
  'bulk-water': { label: 'Bulk Water Carrier', defaultWaterL: 9000, pumpLpm: 800, typicalCrew: 2, isPumper: false },
  support: { label: 'Support / Other', defaultWaterL: 0, pumpLpm: 0, typicalCrew: 2, isPumper: false },
};

export interface ResourceUnit {
  id: string;
  callsign: string;
  agency: string;
  brigade?: string;
  /**
   * Stable id of the real `BrigadeStation` this unit was picked from (see
   * `response-coverage/station.ts` / the statewide NSW RFS seed) — the join
   * key a cross-incident resource-contention check can rely on instead of
   * fuzzy-matching the free-text `brigade` display name. Absent for units
   * added before this field existed, or for agencies with no seeded station
   * dataset (currently NSW RFS only) — never fabricated for those.
   */
  brigadeStationId?: string;
  type: ApplianceType;
  status: ResourceStatus;
  /** Total confirmed crew aboard. */
  crewCount: number;
  /** Confirmed BA operators aboard — a subset of crewCount, never greater than it. */
  baOperators: number;
  onboardWaterL: number;
  etaMin?: number;
}

export interface CreateUnitInput {
  id?: string;
  callsign: string;
  agency?: string;
  brigade?: string;
  brigadeStationId?: string;
  type: ApplianceType;
  status?: ResourceStatus;
  crewCount?: number;
  baOperators?: number;
  onboardWaterL?: number;
  etaMin?: number;
}

/** Clamp BA operators so they can never exceed the total crew aboard. */
const clampBa = (baOperators: number, crewCount: number): number =>
  Math.min(Math.max(0, baOperators), Math.max(0, crewCount));

export function createUnit(input: CreateUnitInput): ResourceUnit {
  const spec = APPLIANCE_REFERENCE[input.type];
  const crewCount = input.crewCount ?? 0;
  return {
    id: input.id ?? `unit_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    callsign: input.callsign,
    agency: input.agency ?? 'NSW RFS',
    brigade: input.brigade,
    brigadeStationId: input.brigadeStationId,
    type: input.type,
    status: input.status ?? 'responding',
    crewCount,
    baOperators: clampBa(input.baOperators ?? 0, crewCount),
    onboardWaterL: input.onboardWaterL ?? spec.defaultWaterL,
    etaMin: input.etaMin,
  };
}

export interface CapabilitySummary {
  unitCount: number;
  crewCount: number;
  baOperators: number;
  pumpers: number;
  onboardWaterL: number;
  /** A dedicated bulk-water/tanker carrying at least the bulk threshold. */
  hasBulkWater: boolean;
  agencies: string[];
}

/** Aggregate capability across the committed units. */
export function summariseCapability(
  units: readonly ResourceUnit[],
  bulkWaterMinL: number,
): CapabilitySummary {
  const committed = units.filter((u) => isCommitted(u.status));
  let crewCount = 0;
  let baOperators = 0;
  let pumpers = 0;
  let onboardWaterL = 0;
  let hasBulkWater = false;
  const agencies = new Set<string>();
  for (const u of committed) {
    crewCount += u.crewCount;
    baOperators += u.baOperators;
    onboardWaterL += u.onboardWaterL;
    if (APPLIANCE_REFERENCE[u.type].isPumper) pumpers += 1;
    if (u.type === 'bulk-water' || u.onboardWaterL >= bulkWaterMinL) hasBulkWater = true;
    agencies.add(u.agency);
  }
  return {
    unitCount: committed.length,
    crewCount,
    baOperators,
    pumpers,
    onboardWaterL,
    hasBulkWater,
    agencies: [...agencies],
  };
}
